import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Ringtone player — estilo "llamada entrante" de un call center: repite el
 * sonido de notificación con cadencia de timbre (no solo un "ding" único) por
 * un tiempo acotado, y se detiene solo cuando el médico actúa (o al agotar
 * los repiques). Evita ruido infinito si el médico está lejos de la pantalla.
 */
const RING_SRC = '/notification.wav';
const RING_INTERVAL_MS = 2500;
const RING_MAX_REPEATS = 8; // ~20s de timbre

export function useRingtone() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<number | null>(null);
  const repeatsRef = useRef(0);

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(RING_SRC);
      audioRef.current.volume = 0.75;
    }
    return audioRef.current;
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    repeatsRef.current = 0;
  }, []);

  const start = useCallback(() => {
    stop();
    const audio = getAudio();
    const ring = () => {
      if (repeatsRef.current >= RING_MAX_REPEATS) {
        stop();
        return;
      }
      repeatsRef.current += 1;
      audio.currentTime = 0;
      audio.play().catch(() => {
        // Bloqueado por política de autoplay hasta el primer gesto del
        // usuario — mismo comportamiento aceptado en useNotificationSound.
      });
    };
    ring();
    intervalRef.current = window.setInterval(ring, RING_INTERVAL_MS);
  }, [getAudio, stop]);

  useEffect(() => stop, [stop]);

  // Memoizado: sin esto, cada render de un consumidor (p. ej.
  // AsignacionEntranteBanner) recibía un objeto NUEVO, disparando de más los
  // efectos que dependen de `ringtone` en su array de dependencias.
  return useMemo(() => ({ start, stop }), [start, stop]);
}
